import { Flex } from 'components';
import Footer from 'components/Footer';
import ReviewSection from 'components/ReviewSection';
import { Intro, Review } from 'data';
import { useSelector, useTrackingLocation } from 'hooks';
import { applyCyAttribute, cdn, css, mq, styled } from 'mymoria-ui';
import {
  ActionButton,
  BulletList,
  Col,
  HeadlineImage,
  PhoneBanner,
  Row,
  Slider,
  Text,
} from 'mymoria-ui/components';
import { flexbox, layout, space } from 'mymoria-ui/custom/styledSystem';
import { FlexboxProps, LayoutProps, SpaceProps } from 'mymoria-ui/interfaces';
import React from 'react';
import { RouteComponentProps } from 'react-router';
import { FUNNEL_STEPS } from 'types';
import { buildGradient, cyNameIntroStep, prepareTrackingDataParams } from 'utils';
import FunnelHeader from '../FunnelHeader';

const { backgroundColor } = Intro;

const StyledActionButton = styled(ActionButton)(() => ({
  maxWidth: 360,
  width: '100%',
}));

const Container = styled.div<LayoutProps & SpaceProps & FlexboxProps>(layout, space, flexbox);

const Body = styled.div(
  ({ theme: { space } }) => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    margin: 0 auto;
    max-width: 1000px;
    ${mq({
      padding: [
        `${space.extraLarge} ${space[6]} 12.5rem ${space[6]}`,
        `7.5rem ${space.extraLarge} 12.5rem ${space.extraLarge}`,
      ],
    })}
  `,
);

const Background = styled.div({
  background: buildGradient(backgroundColor),
  backgroundAttachment: 'fixed',
  backgroundSize: 'cover',
  height: '100%',
  width: '100%',
});

const SliderContainer = styled.div(
  ({ theme: { space } }) => css`
    margin-bottom: ${space.extraLarge};
    ${mq({ display: ['none', 'flex', 'none'] })};
  `,
);

const RowWithGap = styled(Row)(({ theme: { space } }) => ({
  gap: space.small,
}));

const IntroStep = ({ history }: RouteComponentProps) => {
  useTrackingLocation('create.intro', 'funnelpage');
  const {
    headline,
    subHeadlineLeft,
    subHeadlineRight,
    ctaText,
    bullets,
    phoneUpperSection,
    phoneLowerSection,
    teaserBoxSection,
  } = Intro;

  const { _id, Title, GooglePlaceId, GoogleReviewUrl, Status, Headline, SubHeadline, Content } =
    Review;
  const steps = useSelector(({ proposal }) => proposal.steps) || FUNNEL_STEPS;
  const nextStep = () => history.replace(`/create/${steps[0]}`);

  return (
    <>
      <FunnelHeader />
      <Background>
        <Body>
          <Text
            fontFamily="tertiary"
            fontSize={['1.5rem', '2.5rem']}
            fontWeight="bold"
            width="100%"
            mb="extraLarge"
            {...applyCyAttribute(cyNameIntroStep, 'headline')}
          >
            {headline}
          </Text>
          <Flex
            flexDirection={['column', null, 'row']}
            width="100%"
            justifyContent="space-between"
            mb={['normal', null, '7.5rem']}
          >
            <Container width={[1, null, '48%']} mb={['5.5rem', 'extraLarge']}>
              <BulletList
                cyName={cyNameIntroStep}
                headline={subHeadlineLeft || ''}
                list={bullets?.map(val => val.value.Bulletpoint) || []}
                buttonText={ctaText || ''}
                onClick={nextStep}
                ctcTrackingProps={prepareTrackingDataParams(
                  'click',
                  'microconversions',
                  'click',
                  'cta',
                )}
                {...applyCyAttribute(`${cyNameIntroStep}-BulletList`)}
              />
            </Container>
            <Container
              width={[1, null, '48%']}
              display="flex"
              flexDirection="column"
              {...applyCyAttribute(`${cyNameIntroStep}-PhoneBanners`)}
            >
              <Text
                fontWeight="bold"
                fontSize={['medium', '1.5rem']}
                fontFamily="tertiary"
                mb={['small', 7]}
                {...applyCyAttribute(`${cyNameIntroStep}`, 'subHeadlineRight')}
              >
                {subHeadlineRight}
              </Text>
              <PhoneBanner
                PhoneNumberForDisplay={phoneUpperSection?.PhoneNumberDisplayed || ''}
                Image1Path={
                  phoneUpperSection?.Image1.path ? cdn('cms', phoneUpperSection?.Image1.path) : ''
                }
                Image2Path={
                  phoneUpperSection?.Image2?.path ? cdn('cms', phoneUpperSection?.Image2!.path) : ''
                }
                Headline={phoneUpperSection?.Headline || ''}
                onClick={() => {
                  window.open(`tel:${phoneUpperSection?.PhoneNumber}`, '_self');
                }}
                ButtonText={phoneUpperSection?.PhoneNumberDisplayed!}
                width={['100%', '85%', '100%']}
                mb={7}
                CtcTrackingProps={prepareTrackingDataParams(
                  'click',
                  'microconversions',
                  'click',
                  'call_upperNumber',
                )}
                cyName={'phoneUpperSection'}
              />
              <PhoneBanner
                PhoneNumberForDisplay={phoneLowerSection?.PhoneNumberDisplayed || ''}
                Image1Path={
                  phoneLowerSection?.Image1.path ? cdn('cms', phoneLowerSection?.Image1.path) : ''
                }
                Image2Path={
                  phoneLowerSection?.Image2?.path ? cdn('cms', phoneLowerSection?.Image2.path) : ''
                }
                Headline={phoneLowerSection?.Headline || ''}
                onClick={event => {
                  event.stopPropagation();
                  window.open(`tel:${phoneLowerSection?.PhoneNumber}`, '_self');
                }}
                ButtonText={phoneLowerSection?.PhoneNumberDisplayed!}
                ReverseOrder={true}
                width={['100%', '85%', '100%']}
                mb={7}
                CtcTrackingProps={prepareTrackingDataParams(
                  'click',
                  'microconversions',
                  'click',
                  'call_lowerNumber',
                )}
                cyName={'phoneLowerSection'}
              />
            </Container>
          </Flex>
          {teaserBoxSection?.map((teaserBox, idx) => {
            const cyName = `teaserBoxSection${idx}`;
            const {
              value: { RowHeadline, Box, CtaRepeater: shouldDisplayCtaButton },
            } = teaserBox;
            return (
              <React.Fragment key={`teaser-box-${idx}`}>
                <Text
                  fontFamily="tertiary"
                  fontSize={['1.5rem', '2.5rem']}
                  fontWeight="bold"
                  width="100%"
                  mb="normal"
                  textAlign={['center', 'left']}
                  {...applyCyAttribute(`${cyName}`, 'RowHeadline')}
                >
                  {RowHeadline}
                </Text>
                <RowWithGap
                  justifyContent="space-between"
                  flexDirection={['column', 'row']}
                  alignItems={['center', 'unset']}
                  mb={shouldDisplayCtaButton ? 'small' : 'extraLarge'}
                  {...applyCyAttribute(`${cyName}`, 'Box')}
                >
                  {Box.map((box, boxIdx) => {
                    const {
                      value: { TeaserHeadline, TeaserText, TeaserImage },
                    } = box;
                    return (
                      <Col
                        key={`teaser-box-${idx}-box-${boxIdx}`}
                        display={['flex', 'none', 'flex']}
                        px="0px"
                        {...applyCyAttribute(`${cyName}`, `Box${boxIdx}`)}
                      >
                        <HeadlineImage
                          cyName={'HeadlineImage'}
                          headline={TeaserHeadline}
                          teaser={TeaserText}
                          path={cdn('cms', TeaserImage.path)}
                          mb={boxIdx === Box.length - 1 ? 0 : ['medium', 0]}
                          maxWidth={['320px', '425px', '355px', '355px']}
                          height={['320px', '425px', '355px', '355px']}
                        />
                      </Col>
                    );
                  })}
                </RowWithGap>
                <SliderContainer>
                  <Slider slidesLength={Box.length} {...applyCyAttribute(`${cyName}`, 'Slider')}>
                    {Box.map((box, boxIdx) => {
                      const {
                        value: { TeaserHeadline, TeaserText, TeaserImage },
                      } = box;
                      return (
                        <HeadlineImage
                          cyName={'HeadlineImage'}
                          key={`teaser-box-${idx}-box-${boxIdx}`}
                          headline={TeaserHeadline}
                          teaser={TeaserText}
                          path={cdn('cms', TeaserImage.path)}
                          maxWidth={['320px', '425px', '355px', '355px']}
                          height={['320px', '425px', '355px', '355px']}
                          {...applyCyAttribute(`${cyName}`, `Slider${idx}${boxIdx}`)}
                        />
                      );
                    })}
                  </Slider>
                </SliderContainer>
                {shouldDisplayCtaButton && (
                  <Row justifyContent="center" mb={7}>
                    <StyledActionButton
                      onClick={nextStep}
                      {...prepareTrackingDataParams('click', 'microconversions', 'click', 'cta')}
                      {...applyCyAttribute(`${cyName}`, 'StyledActionButton')}
                    >
                      {ctaText || ''}
                    </StyledActionButton>
                  </Row>
                )}
              </React.Fragment>
            );
          })}
          <ReviewSection
            _id={_id}
            Title={Title}
            GooglePlaceId={GooglePlaceId}
            GoogleReviewUrl={GoogleReviewUrl}
            Status={Status}
            Headline={Headline}
            SubHeadline={SubHeadline}
            Content={Content}
            cyName={cyNameIntroStep}
          />
        </Body>
      </Background>
      <Footer />
    </>
  );
};

export default IntroStep;
